import { memo, useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { Exchange, exchangeToJSON } from '../../proto/generated/common'
import { selectPositionLtpsSignature } from '../../selectors/security'
import { buildLtpsByKey } from '../../util/positionLtpsSignature'
import { getSecurityKey, getTwinSecurityKey } from '../../util/security'
import type { Security } from '../../proto/generated/security'

export type QuickTradeExchangeSelectorProps = {
    availableExchanges: Exchange[]
    selectedSecurity: Security
    type: string
    onExchangeChange: (exchange: Exchange) => void
}

const formatExchangePrice = (ltp: number, priceMultiplier: number) =>
    ltp > 0 ? (ltp / priceMultiplier).toFixed(2) : '-'

type ExchangePillTabProps = {
    exchange: Exchange
    selected: boolean
    price: string
    onSelect: (exchange: Exchange) => void
}

const ExchangePillTab = memo(function ExchangePillTab({
    exchange,
    selected,
    price,
    onSelect,
}: ExchangePillTabProps) {
    return (
        <Box
            component="button"
            type="button"
            onClick={() => onSelect(exchange)}
            sx={(theme) => ({
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                border: '1px solid',
                borderColor: selected ? 'text.primary' : 'divider',
                borderRadius: '20px',
                py: 0.375,
                px: 1.25,
                minHeight: 28,
                cursor: 'pointer',
                bgcolor: 'background.paper',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
                whiteSpace: 'nowrap',
                fontFamily: 'inherit',
                '&:hover': {
                    bgcolor: theme.palette.action.hover,
                },
            })}
        >
            <Typography
                component="span"
                fontSize={11}
                fontWeight={500}
                color={selected ? 'text.primary' : 'text.secondary'}
                lineHeight={1.2}
            >
                {exchangeToJSON(exchange)}
            </Typography>
            <Typography
                component="span"
                className="font-feature-num"
                fontSize={12}
                fontWeight={500}
                color="text.primary"
                lineHeight={1.2}
            >
                {price}
            </Typography>
        </Box>
    )
})

const QuickTradeExchangeSelector: React.FC<QuickTradeExchangeSelectorProps> = ({
    availableExchanges,
    selectedSecurity,
    type,
    onExchangeChange,
}) => {
    const sortedExchanges = useMemo(
        () => [...availableExchanges].sort((a, b) => a - b),
        [availableExchanges]
    )

    const exchangeKeys = useMemo(
        () =>
            sortedExchanges
                .map((e) => getTwinSecurityKey(selectedSecurity, e) ?? getSecurityKey(e, selectedSecurity.code))
                .filter((key): key is string => !!key),
        [sortedExchanges, selectedSecurity]
    )

    const ltpsSignature = useSelector(selectPositionLtpsSignature(exchangeKeys))
    const ltpsByKey = useMemo(
        () => buildLtpsByKey(exchangeKeys, ltpsSignature),
        [exchangeKeys, ltpsSignature]
    )
    const priceMultiplier = selectedSecurity.priceMultiplier ?? 100

    if (availableExchanges.length <= 1) {
        return null
    }

    if (type === 'modify') {
        const securityKey = getSecurityKey(selectedSecurity.exchange, selectedSecurity.code)
        const ltp = ltpsByKey.get(securityKey) ?? 0
        const price = formatExchangePrice(ltp, priceMultiplier)

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                <Typography fontSize="12px" color="text.primary">
                    {exchangeToJSON(selectedSecurity.exchange)}
                </Typography>
                <Typography fontSize="12px" color="text.primary" className="font-feature-num">
                    ₹{price}
                </Typography>
            </Box>
        )
    }

    return (
        <Box
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}
        >
            {sortedExchanges.map((exchange) => {
                const securityKey =
                    getTwinSecurityKey(selectedSecurity, exchange) ??
                    getSecurityKey(exchange, selectedSecurity.code)
                const ltp = ltpsByKey.get(securityKey) ?? 0
                const price = formatExchangePrice(ltp, priceMultiplier)

                return (
                    <ExchangePillTab
                        key={exchange}
                        exchange={exchange}
                        selected={selectedSecurity.exchange === exchange}
                        price={price}
                        onSelect={onExchangeChange}
                    />
                )
            })}
        </Box>
    )
}

export default memo(QuickTradeExchangeSelector)
